"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLocale } from "next-intl";
import toast from "react-hot-toast";
import { Bell } from "lucide-react";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { getEcho } from "@/services/echo";
import {
  getNotifications,
  markAsRead as markAsReadApi,
  markAllAsRead as markAllAsReadApi,
  type Notification,
} from "@/services/notifications";

type LocalizedText = { en?: string; ar?: string } | undefined;

function getLocalizedContent(data: LocalizedText, locale: string): string {
  if (!data) return "";
  return locale === "ar" ? data.ar || data.en || "" : data.en || data.ar || "";
}

type NotificationContextValue = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  page: number;
  totalPages: number;
  fetchNotifications: (targetPage?: number, isLoadMore?: boolean) => Promise<void>;
  loadMore: () => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const locale = useLocale();
  const { user, isAuthenticated } = useCurrentUser();

  const fetchNotifications = useCallback(async (targetPage = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const data = await getNotifications(targetPage);

      setNotifications((prev) =>
        isLoadMore ? [...prev, ...data.notifications.data] : data.notifications.data
      );
      setUnreadCount(data.unread_count);
      setTotalPages(data.notifications.last_page);
      setPage(data.notifications.current_page);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (page < totalPages) {
      fetchNotifications(page + 1, true);
    }
  }, [page, totalPages, fetchNotifications]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(1);
    }
  }, [isAuthenticated, fetchNotifications]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    const echo = getEcho();
    if (!echo) {
      return;
    }

    const channelName = `App.Models.User.${user.id}`;

    echo.private(channelName).notification((payload: any) => {
      const newNotif: Notification = {
        id: payload.id || Math.random().toString(36).slice(2),
        type: "broadcast",
        data: {
          title: payload.title,
          message: payload.message,
          link: payload.link,
          category: payload.category,
          priority: payload.priority,
        },
        read_at: null,
        created_at: payload.created_at || new Date().toISOString(),
      };

      setNotifications((prev) => {
        if (prev.some((n) => n.id === newNotif.id)) return prev;
        return [newNotif, ...prev];
      });
      setUnreadCount((prev) => prev + 1);

      const localizedTitle = getLocalizedContent(newNotif.data.title, locale);
      const localizedMessage = getLocalizedContent(newNotif.data.message, locale);

      toast.success(
        <div className="flex flex-col gap-1">
          {localizedTitle && <strong className="text-sm">{localizedTitle}</strong>}
          <span className="text-xs">{localizedMessage}</span>
        </div>,
        {
          duration: 5000,
          icon: <Bell size={18} className="text-[#43ADD8]" />,
        }
      );
    });

    return () => {
      echo.leave(channelName);
    };
  }, [isAuthenticated, user?.id, locale]);

  const markAsRead = async (id: string) => {
    try {
      await markAsReadApi(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllAsReadApi();
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        loadingMore,
        page,
        totalPages,
        fetchNotifications,
        loadMore,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}
